import { useEffect, useMemo, useRef, useState } from "react";
import { decodePcm, PttControlType, type Call } from "@mcptt/client";
import { useMCPTT } from "../mcptt/hooks";

/**
 * Live event log spanning the bottom of the shell.
 *
 * Subscribes to PTT / EMG / ACK events from the client and appends rows.
 * Capped to MAX_EVENTS in memory; persisted to sessionStorage so a
 * page reload (within the tab) keeps the recent log visible. Cleared
 * on sign-out by the provider.
 */

const MAX_EVENTS    = 250;
const STORAGE_KEY   = "mcptt:eventlog:v1";

type EventKind = "PTT" | "CALL" | "EMG" | "ACK";

interface EventRow {
  id:       string;
  ts:       number;       // wall-clock ms; formatted on render
  type:     EventKind;
  unit:     string;
  channel:  string;
  detail:   string;
  control?: PttControlType;
  callId?:   string;
  targetId?: string;
  networkId?: string;
  startedAt?: number;
  endedAt?: number;
}

interface FilterChip {
  key:   "ALL" | EventKind;
  label: string;
  match: (e: EventRow) => boolean;
}

const FILTERS: FilterChip[] = [
  { key: "ALL",  label: "ALL",  match: () => true },
  { key: "PTT",  label: "PTT",  match: (e) => e.type === "PTT" },
  { key: "CALL", label: "CALL", match: (e) => e.type === "CALL" },
  { key: "EMG",  label: "EMG",  match: (e) => e.type === "EMG" },
  { key: "ACK",  label: "ACK",  match: (e) => e.type === "ACK" },
];

export function EventLogPanel() {
  const { client } = useMCPTT();
  const [events, setEvents] = useState<EventRow[]>(() => loadFromStorage());
  const [filter, setFilter] = useState<FilterChip["key"]>("ALL");
  const [replayNotice, setReplayNotice] = useState<string | null>(null);
  const [activeReplayId, setActiveReplayId] = useState<string | null>(null);
  const seqRef = useRef(0);
  const audioRef = useRef<Map<string, Uint8Array[]>>(new Map());

  // Persist to sessionStorage on change (debounced via microtask).
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(events));
      } catch { /* private mode / quota */ }
    });
    return () => { cancelled = true; };
  }, [events]);

  // Subscribe to live events.
  useEffect(() => {
    function pushRow(row: Omit<EventRow, "id" | "ts">) {
      const id = `${Date.now()}-${++seqRef.current}`;
      const ts = Date.now();
      setEvents((prev) => {
        const next = [{ id, ts, ...row }, ...prev];
        return next.length > MAX_EVENTS ? next.slice(0, MAX_EVENTS) : next;
      });
    }

    function upsertPtt(call: Call, control: PttControlType) {
      const now = Date.now();
      setEvents((prev) => {
        const idx = prev.findIndex((e) => e.callId === call.CallID);
        const existing = idx >= 0 ? prev[idx] : undefined;
        const startedAt = existing?.startedAt ?? call.startedAt ?? now;
        const ended = control === PttControlType.VoiceGroupEnd
                   || control === PttControlType.VoicePrivateEnd;
        const released = control === PttControlType.VoiceGroupReleased
                      || control === PttControlType.VoicePrivateReleased;
        const pressed = control === PttControlType.VoiceGroupPressed
                     || control === PttControlType.VoicePrivatePressed;
        const endedAt = ended ? now : existing?.endedAt;
        const durationMs = (endedAt ?? now) - startedAt;
        const detail = ended
          ? `Call ended · ${formatDuration(durationMs)}`
          : released
            ? `Released · ${formatDuration(durationMs)}`
            : pressed
              ? `Talking · ${formatDuration(durationMs)}`
              : existing?.detail ?? "Call begin";
        const row: EventRow = {
          id: existing?.id ?? `${now}-${++seqRef.current}`,
          ts: existing?.ts ?? now,
          type: "PTT",
          unit: call.SourceName ?? call.SourceID.slice(0, 6),
          channel: call.TargetName ?? call.TargetID.slice(0, 6),
          detail,
          control,
          callId: call.CallID,
          targetId: call.TargetID,
          networkId: call.NetworkID,
          startedAt,
          endedAt,
        };
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = row;
          return next;
        }
        const next = [row, ...prev];
        return next.length > MAX_EVENTS ? next.slice(0, MAX_EVENTS) : next;
      });
    }

    const offPtt = client.on("ptt", (call: Call, control: PttControlType) => {
      if (!isVoiceControl(control)) return;
      upsertPtt(call, control);
    });

    const offVoice = client.on("voice", (call: Call, frame: Uint8Array) => {
      const arr = audioRef.current.get(call.CallID) ?? [];
      arr.push(frame.slice(28)); // strip [12-byte RTP][16-byte CallID]
      audioRef.current.set(call.CallID, arr);
    });

    const offEmergency = client.on("emergency", (msg) => {
      pushRow({
        type:    "EMG",
        unit:    msg.UserName,
        channel: "—",
        detail:  `SOS triggered (reason ${msg.Reason})`,
      });
    });

    const offAck = client.on("emergency:ack", (msg) => {
      pushRow({
        type:    "ACK",
        unit:    msg.UserName,
        channel: "—",
        detail:  `Acknowledged ${msg.EmergencyID.slice(0, 6)}`,
      });
    });

    return () => { offPtt(); offVoice(); offEmergency(); offAck(); };
  }, [client]);

  const counts = useMemo(() => {
    const c = { ALL: events.length, PTT: 0, CALL: 0, EMG: 0, ACK: 0 } as Record<FilterChip["key"], number>;
    for (const e of events) c[e.type]++;
    return c;
  }, [events]);

  const visible = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter)!;
    return events.filter(f.match);
  }, [events, filter]);

  async function replayCall(e: EventRow) {
    if (!e.callId) return;
    const chunks = audioRef.current.get(e.callId) ?? [];
    if (chunks.length === 0) return;
    console.info("[mcptt] Event log replay", {
      channel: e.channel,
      callId: e.callId,
      chunks: chunks.length,
    });
    setActiveReplayId(e.id);
    setReplayNotice(`REPLAY · ${e.channel}`);
    try {
      await playChunks(chunks);
    } finally {
      setActiveReplayId(null);
      window.setTimeout(() => {
        setReplayNotice((cur) => cur === `REPLAY · ${e.channel}` ? null : cur);
      }, 1200);
    }
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="ph-title">Event Log</span>
        <span className="ph-meta">LIVE · {events.length}</span>
      </div>
      <div className="panel-toolbar">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`chip ${filter === f.key ? "selected" : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label} · {counts[f.key]}
            </button>
          ))}
        </div>
        {replayNotice ? <span className="event-reply-notice">{replayNotice}</span> : null}
        <button
          type="button"
          className="chip"
          onClick={() => {
            setEvents([]);
            try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
          }}
          title="Clear the log (does not affect other dispatchers)"
        >
          CLEAR
        </button>
      </div>
      <div className="panel-body">
        <div className="evt-head">
          <span>TIME</span>
          <span>EVENT</span>
          <span>UNIT</span>
          <span>CHANNEL · DETAIL</span>
        </div>
        {visible.length === 0 ? (
          <div className="empty">
            {events.length === 0 ? "Listening for events…" : "No events match this filter"}
          </div>
        ) : (
          visible.map((e) => (
            <div key={e.id} className="evt-row">
              <span className="evt-time">{formatTime(new Date(e.ts))}</span>
              <span className={`evt-type ${typeClass(e)}`}>{e.type}</span>
              <span className="evt-unit">{e.unit}</span>
              <span className="evt-detail">
                <span className="t-mono" style={{ color: "var(--steel-500)", marginRight: 8, fontSize: 12 }}>
                  {e.channel}
                </span>
                {e.detail}
                {e.type === "PTT" ? (
                  <span className="evt-actions">
                    <button
                      type="button"
                      className={`evt-action ${activeReplayId === e.id ? "is-live" : ""}`}
                      disabled={!e.callId || !(audioRef.current.get(e.callId)?.length)}
                      title={!e.callId || !(audioRef.current.get(e.callId)?.length)
                        ? "No current-session audio buffer captured for replay"
                        : "Replay captured call audio locally"}
                      onClick={() => { void replayCall(e); }}
                    >
                      {activeReplayId === e.id ? "PLAY" : "REPLAY"}
                    </button>
                    <button
                      type="button"
                      className="evt-action"
                      disabled={!e.callId || !(audioRef.current.get(e.callId)?.length)}
                      title={!e.callId || !(audioRef.current.get(e.callId)?.length)
                        ? "No current-session audio buffer captured for this row. WAV is only available for calls received since this page loaded."
                        : "Download call audio as WAV"}
                      onClick={() => {
                        if (!e.callId) return;
                        downloadWav(e, audioRef.current.get(e.callId) ?? []);
                      }}
                    >
                      WAV
                    </button>
                  </span>
                ) : null}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function loadFromStorage(): EventRow[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Sanity-cap.
    return parsed.slice(0, MAX_EVENTS) as EventRow[];
  } catch {
    return [];
  }
}

function isVoiceControl(c: PttControlType): boolean {
  switch (c) {
    case PttControlType.VoiceGroupBegin:
    case PttControlType.VoicePrivateBegin:
    case PttControlType.VoiceGroupEnter:
    case PttControlType.VoicePrivateEnter:
    case PttControlType.VoiceGroupPressed:
    case PttControlType.VoicePrivatePressed:
    case PttControlType.VoiceGroupReleased:
    case PttControlType.VoicePrivateReleased:
    case PttControlType.VoiceGroupEnd:
    case PttControlType.VoicePrivateEnd:
      return true;
    default:
      return false;
  }
}

function typeClass(e: EventRow): string {
  if (e.type === "EMG" || e.type === "ACK") return "emg";
  if (e.control === PttControlType.VoiceGroupPressed
   || e.control === PttControlType.VoicePrivatePressed) return "tx";
  return "ptt";
}

function formatTime(d: Date): string {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function downloadWav(row: EventRow, chunks: Uint8Array[]) {
  if (chunks.length === 0) return;
  const bytes = concat(chunks);
  const pcm = decodePcm(bytes, "alaw");
  const wav = makeWav(pcm, 16000);
  const blob = new Blob([wav], { type: "audio/wav" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${safe(row.unit)}-${safe(row.channel)}-${row.callId?.slice(0, 6) ?? "call"}.wav`;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(a.href);
  a.remove();
}

async function playChunks(chunks: Uint8Array[]) {
  const bytes = concat(chunks);
  const pcm = decodePcm(bytes, "alaw");
  const ctx = new AudioContext({ latencyHint: "interactive", sampleRate: 16000 });
  if (ctx.state === "suspended") await ctx.resume();
  const buffer = ctx.createBuffer(1, pcm.length, 16000);
  buffer.copyToChannel(pcm, 0);
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.connect(ctx.destination);
  await new Promise<void>((resolve) => {
    src.onended = () => resolve();
    src.start();
  });
  await ctx.close().catch(() => {});
}

function concat(chunks: Uint8Array[]): Uint8Array {
  const len = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(len);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}

function makeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const dataBytes = samples.length * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const v = new DataView(buffer);
  writeAscii(v, 0, "RIFF");
  v.setUint32(4, 36 + dataBytes, true);
  writeAscii(v, 8, "WAVE");
  writeAscii(v, 12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  writeAscii(v, 36, "data");
  v.setUint32(40, dataBytes, true);
  let off = 44;
  for (const s of samples) {
    const clamped = Math.max(-1, Math.min(1, s));
    v.setInt16(off, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    off += 2;
  }
  return buffer;
}

function writeAscii(v: DataView, off: number, s: string) {
  for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i));
}

function safe(s: string): string {
  return s.replace(/[^a-z0-9_-]+/gi, "_").slice(0, 32);
}
